import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Save,
  Loader2,
  ToggleLeft,
  ToggleRight,
  FileText,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettingsPage({ admin }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('features');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (key) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: !prev.features?.[key]
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const features = [
    { key: 'posts_enabled', label: 'Posts', description: 'Allow users to create posts' },
    { key: 'comments_enabled', label: 'Comments', description: 'Allow comments on posts' },
    { key: 'messages_enabled', label: 'Messages', description: 'Enable direct messaging' },
    { key: 'live_aarti_enabled', label: 'Live Aarti', description: 'Show live temple streams' },
    { key: 'granth_enabled', label: 'Granth Section', description: 'Enable sacred texts reading' },
    { key: 'reels_enabled', label: 'Reels/Shorts', description: 'Enable short video content' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Settings</h1>
          <p className="text-slate-500">Configure platform-wide settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Save Changes
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200">
          <nav className="flex">
            {[
              { id: 'features', label: 'Feature Toggles', icon: ToggleRight },
              { id: 'policies', label: 'Policies & Guidelines', icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Feature Toggles */}
          {activeTab === 'features' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 mb-6">
                Enable or disable features across the entire platform.
              </p>
              
              {features.map(feature => (
                <div
                  key={feature.key}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div>
                    <h3 className="font-medium text-slate-900">{feature.label}</h3>
                    <p className="text-sm text-slate-500">{feature.description}</p>
                  </div>
                  <button
                    onClick={() => toggleFeature(feature.key)}
                    className={`p-1 rounded-full transition-colors ${
                      settings?.features?.[feature.key]
                        ? 'text-green-500'
                        : 'text-slate-300'
                    }`}
                  >
                    {settings?.features?.[feature.key] ? (
                      <ToggleRight className="w-10 h-10" />
                    ) : (
                      <ToggleLeft className="w-10 h-10" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Policies */}
          {activeTab === 'policies' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Privacy Policy
                </label>
                <textarea
                  value={settings?.privacy_policy || ''}
                  onChange={(e) => setSettings({ ...settings, privacy_policy: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="Enter your privacy policy content..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Terms of Service
                </label>
                <textarea
                  value={settings?.terms_of_service || ''}
                  onChange={(e) => setSettings({ ...settings, terms_of_service: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="Enter your terms of service..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Community Guidelines
                </label>
                <textarea
                  value={settings?.community_guidelines || ''}
                  onChange={(e) => setSettings({ ...settings, community_guidelines: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="Enter community guidelines aligned with Sanatan Dharma values..."
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
